import { useState, useEffect } from 'react'
import { collection, query, where, orderBy, limit, onSnapshot, type QuerySnapshot, type DocumentData } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useAuth } from '../contexts/AuthContext'

/**
 * Individual workflow step status
 */
export type StepStatus = 'pending' | 'active' | 'complete' | 'error'

/**
 * The 5 workflow steps for a case
 */
export interface WorkflowSteps {
  documents: StepStatus    // Step 1: Document upload & scanning
  income: StepStatus       // Step 2: Income verification
  cashflow: StepStatus     // Step 3: Cash flow analysis (future)
  assets: StepStatus       // Step 4: Assets verification (future)
  debt: StepStatus         // Step 5: Debt analysis (future)
}

export interface CaseSummary {
  id: string
  shortId: string            // First 5 chars of UUID for display
  borrowerName: string
  steps: WorkflowSteps
  completedSteps: number
  totalSteps: number
  currentStep: string        // Human readable current step
  statusMessage: string      // Detail message
  documentsCount: number     // Count of documents (from case doc)
  createdAt: Date
  updatedAt: Date
}

interface UseCasesResult {
  cases: CaseSummary[]
  loading: boolean
  error: Error | null
}

/**
 * Hook to subscribe to the user's cases from Firestore.
 * Returns a summarized list suitable for the sidebar case list.
 */
export function useCases(): UseCasesResult {
  const { user } = useAuth()
  const [cases, setCases] = useState<CaseSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!user) {
      setCases([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    // Query cases for this user, ordered by creation date (newest first)
    const casesQuery = query(
      collection(db, 'cases'),
      where('uid', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(50)
    )

    const unsubscribe = onSnapshot(
      casesQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const caseSummaries: CaseSummary[] = []

        snapshot.forEach((doc) => {
          const data = doc.data()
          caseSummaries.push(transformCaseData(doc.id, data))
        })

        setCases(caseSummaries)
        setLoading(false)
      },
      (err) => {
        console.error('Error fetching cases:', err)

        // Handle missing index error - retry without orderBy
        if (err.code === 'failed-precondition' || err.message?.includes('index')) {
          console.warn('Missing Firestore index, retrying without orderBy')
          retryWithoutOrderBy(user.uid, setCases, setLoading, setError)
        } else {
          setError(err)
          setLoading(false)
        }
      }
    )

    return () => unsubscribe()
  }, [user])

  return { cases, loading, error }
}

/**
 * Transform Firestore document data into CaseSummary
 */
function transformCaseData(id: string, data: DocumentData): CaseSummary {
  // Extract borrower name from either format
  const borrowerInfo = data.borrower_info || data.borrower || {}
  const borrowerName = borrowerInfo.full_name || borrowerInfo.fullName || 'Unknown Borrower'

  // Derive workflow steps status
  const { steps, currentStep, statusMessage } = deriveWorkflowSteps(data)

  // Count completed steps
  const stepValues = Object.values(steps)
  const completedSteps = stepValues.filter(s => s === 'complete').length
  const totalSteps = stepValues.length

  // Parse dates
  const createdAt = data.createdAt?.toDate?.() || new Date()
  const updatedAt = data.updatedAt?.toDate?.() || data.createdAt?.toDate?.() || new Date()

  // Use denormalized documentsCount (avoids N subcollection queries in case list)
  const documentsCount = data.documentsCount || 0

  return {
    id,
    shortId: id.substring(0, 5),
    borrowerName,
    steps,
    completedSteps,
    totalSteps,
    currentStep,
    statusMessage,
    documentsCount,
    createdAt,
    updatedAt,
  }
}

/**
 * Derive status of each workflow step from case data
 */
function deriveWorkflowSteps(data: DocumentData): {
  steps: WorkflowSteps
  currentStep: string
  statusMessage: string
} {
  const incomeVerification = data.incomeVerification
  const documentScan = data.documentScan
  // Use denormalized documentsCount from case doc
  const documentCount = data.documentsCount || 0
  // For scanned count, we rely on documentScan status since classifications are now in subcollection
  // When status is awaiting_approval or approved, docs have been scanned
  const scannedCount = documentCount // Assume all uploaded docs are scanned when status reflects it

  // Initialize all steps as pending
  const steps: WorkflowSteps = {
    documents: 'pending',
    income: 'pending',
    cashflow: 'pending',
    assets: 'pending',
    debt: 'pending',
  }

  let currentStep = 'Add documents'
  let statusMessage = 'No documents'

  // Step 1: Documents
  if (documentCount === 0) {
    steps.documents = 'pending'
    currentStep = 'Add documents'
    statusMessage = 'Upload documents to begin'
  } else if (documentScan?.status === 'scanning' || documentScan?.status === 'running') {
    steps.documents = 'active'
    currentStep = 'Scanning'
    statusMessage = `Scanning ${documentCount} doc${documentCount !== 1 ? 's' : ''}`
  } else if (documentScan?.status === 'awaiting_approval') {
    steps.documents = 'active'
    currentStep = 'Review docs'
    statusMessage = `${scannedCount} doc${scannedCount !== 1 ? 's' : ''} ready for review`
  } else if (documentScan?.status === 'approved' || incomeVerification?.currentPhase >= 1) {
    steps.documents = 'complete'
    currentStep = 'Income'
    statusMessage = `${scannedCount} doc${scannedCount !== 1 ? 's' : ''} approved`
  } else if (scannedCount > 0) {
    steps.documents = 'active'
    currentStep = 'Scanning'
    statusMessage = `${scannedCount}/${documentCount} scanned`
  } else {
    steps.documents = 'active'
    currentStep = 'Documents'
    statusMessage = `${documentCount} doc${documentCount !== 1 ? 's' : ''} uploaded`
  }

  // Step 2: Income verification
  if (incomeVerification?.status === 'verified') {
    steps.documents = 'complete'
    steps.income = 'complete'
    currentStep = 'Complete'
    statusMessage = 'Income verified'
  } else if (incomeVerification?.status === 'blocked') {
    steps.documents = 'complete'
    steps.income = 'error'
    currentStep = 'Blocked'
    statusMessage = incomeVerification.blockReason || 'Review needed'
  } else if (incomeVerification?.currentPhase > 1) {
    steps.documents = 'complete'
    steps.income = 'active'
    currentStep = 'Analyzing'
    statusMessage = incomeVerification.currentStep || 'Analyzing income'
  }

  // Future workflows (3, 4, 5) remain pending for now

  return { steps, currentStep, statusMessage }
}

/**
 * Fallback query without orderBy if index is missing
 */
function retryWithoutOrderBy(
  uid: string,
  setCases: React.Dispatch<React.SetStateAction<CaseSummary[]>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<Error | null>>
) {
  const fallbackQuery = query(
    collection(db, 'cases'),
    where('uid', '==', uid),
    limit(50)
  )

  onSnapshot(
    fallbackQuery,
    (snapshot) => {
      const caseSummaries: CaseSummary[] = []

      snapshot.forEach((doc) => {
        const data = doc.data()
        caseSummaries.push(transformCaseData(doc.id, data))
      })

      // Sort client-side since we can't use orderBy
      caseSummaries.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())

      setCases(caseSummaries)
      setLoading(false)
    },
    (err) => {
      console.error('Fallback query also failed:', err)
      setError(err)
      setLoading(false)
    }
  )
}
